import { formatDate } from '../utils/dateConverter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CallOutcomeModal from '../modals/CallOutcomeModal';
import { getFollowUps } from '../services/AcquisitionService';
import { getLeads, patchLead } from '../services/LeadService';
import api from '../services/api';
import { buildCallList, dealToCallItem, groupByBucket, isoInDays } from '../utils/callList';
import { formatEurShort } from '../utils/salesPipeline';
import { extractApiError } from '../utils/apiError';
import { useToast } from '../context/UIContext';
import '../styles/CallList.css';

const fmtDate = (value) => (value ? formatDate(value) : '—');

const bucketNote = (item) => {
    if (item.bucket === 'overdue') {
        const days = item.daysOverdue ?? 0;
        return `${days} Tag${days === 1 ? '' : 'e'} überfällig`;
    }
    if (item.bucket === 'today') return 'heute fällig';
    return 'kein nächster Schritt';
};

/** "Wen muss ich heute anrufen": deals and leads in one worklist. `onOpenDeal` lets the host page
 *  open its detail drawer instead of navigating. */
export default function CallList({ onOpenDeal, onDataChange, reloadToken = 0 }) {
    const toast = useToast();
    const [scope, setScope] = useState('mine');
    const [deals, setDeals] = useState([]);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [callItem, setCallItem] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return Promise.all([
            getFollowUps({ scope }),
            getLeads({ scope }),
        ])
            .then(([followUps, leadList]) => {
                setDeals((followUps.items || []).map(dealToCallItem));
                setLeads(leadList);
                setLoadError('');
            })
            .catch(() => setLoadError('Anrufliste konnte nicht geladen werden.'))
            .finally(() => setLoading(false));
    }, [scope]);

    useEffect(() => { load(); }, [load, reloadToken]);

    const items = useMemo(() => buildCallList(deals, leads), [deals, leads]);
    const groups = useMemo(() => groupByBucket(items), [items]);

    const afterChange = () => {
        setCallItem(null);
        load();
        onDataChange?.();
    };

    const snooze = (item, days) => {
        const date = isoInDays(days);
        const request = item.kind === 'deal'
            ? api.patch(`/ProjectAcquisition/${item.id}`, { next_follow_up_date: date })
            : patchLead(item.id, { next_follow_up_date: date });
        request
            .then(afterChange)
            .catch(err => toast(extractApiError(err, 'Verschieben fehlgeschlagen.')));
    };

    return (
        <section className="calls">
            <div className="calls-head">
                <div className="calls-head-left">
                    <h3 className="calls-title">
                        ☎ Heute anrufen
                        {!loading && <span className="calls-count">{items.length}</span>}
                    </h3>
                    <span className="calls-sub">
                        Überfällige und heute fällige Wiedervorlagen — Deals und Leads
                    </span>
                </div>
                <div className="view-toggle calls-scope">
                    <button
                        className={`view-btn ${scope === 'mine' ? 'view-btn-active' : ''}`}
                        onClick={() => setScope('mine')}
                    >
                        Meine
                    </button>
                    <button
                        className={`view-btn ${scope === 'all' ? 'view-btn-active' : ''}`}
                        onClick={() => setScope('all')}
                    >
                        Alle
                    </button>
                </div>
            </div>

            {loadError && (
                <div className="alert alert--error">
                    {loadError}
                    <button className="alert__close" onClick={load} style={{ marginLeft: 12 }}>
                        Erneut versuchen
                    </button>
                </div>
            )}

            {loading && items.length === 0 && <p className="calls-empty">Wird geladen…</p>}

            {!loading && items.length === 0 && !loadError && (
                <p className="calls-empty calls-empty--done">
                    ✓ Für heute nichts offen{scope === 'mine' ? ' — bei deinen Deals und Leads' : ''}.
                </p>
            )}

            {groups.map(group => (
                <div key={group.key} className="calls-group">
                    <div className={`calls-group-head is-${group.key}`} title={group.hint}>
                        {group.label}
                        <span className="calls-group-count">{group.items.length}</span>
                    </div>
                    <ul className="calls-list">
                        {group.items.map(item => (
                            <li key={`${item.kind}-${item.id}`} className="calls-row">
                                <span className={`calls-kind is-${item.kind}`}>
                                    {item.kind === 'deal' ? 'Deal' : 'Lead'}
                                </span>

                                <button
                                    className="calls-main"
                                    onClick={() => (item.kind === 'deal'
                                        ? onOpenDeal?.(item.id)
                                        : setCallItem(item))}
                                    title={item.kind === 'deal' ? 'Deal öffnen' : 'Anruf protokollieren'}
                                >
                                    <span className="calls-company">{item.company}</span>
                                    <span className="calls-meta">
                                        {item.contact && <span>{item.contact}</span>}
                                        <span className="calls-stage">{item.stageLabel}</span>
                                        <span className={`calls-flag is-${item.bucket}`}>{bucketNote(item)}</span>
                                        {item.attempts > 0 && (
                                            <span title={`Letzter Kontakt: ${fmtDate(item.lastContact)}`}>
                                                {item.attempts} Versuch{item.attempts === 1 ? '' : 'e'}
                                            </span>
                                        )}
                                        {item.value > 0 && <span>{formatEurShort(item.value)}</span>}
                                    </span>
                                </button>

                                {item.phone ? (
                                    <a className="calls-phone" href={`tel:${item.phone}`} title="Anrufen">
                                        📞 {item.phone}
                                    </a>
                                ) : (
                                    <span className="calls-phone calls-phone--missing" title="Keine Telefonnummer hinterlegt">
                                        keine Nummer
                                    </span>
                                )}

                                <div className="calls-actions">
                                    <button className="button" onClick={() => setCallItem(item)}>
                                        Anruf
                                    </button>
                                    <button
                                        className="button button-secondary"
                                        onClick={() => snooze(item, 1)}
                                        title="Auf morgen verschieben"
                                    >
                                        Morgen
                                    </button>
                                    <button
                                        className="button button-secondary"
                                        onClick={() => snooze(item, 7)}
                                        title="Auf nächste Woche verschieben"
                                    >
                                        +1 Wo.
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            {callItem && (
                <CallOutcomeModal
                    item={callItem}
                    onClose={() => setCallItem(null)}
                    onSaved={afterChange}
                />
            )}
        </section>
    );
}
